import api from './api.service';
import type { RejectionPayload } from './rejection.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface ReceivablePOLineDTO {
  id: string;
  po_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string;
  item_category: string | null;
  item_type?: string;
  ordered_quantity: number;
  received_quantity: number;
  /** Qty on Draft/Submitted/Approved GRNs not yet posted. */
  in_process_quantity?: number;
  pending_qty: number;
  uom: string | null;
  project_id: string | null;
  project?: { id: string; project_number?: string | null; project_name?: string | null };
  unit_price: number;
  specifications?: string | null;
  supplier_description?: string | null;
  stock_quantity?: number | null;
  stock_uom?: string | null;
  default_stock_item_id?: string | null;
  /** Theoretical weight (kg) for the whole ordered quantity, carried from the PR. */
  theoretical_weight?: number | null;
  weight_tolerance_plus_pct?: number | null;
  weight_tolerance_minus_pct?: number | null;
  is_critical?: boolean;
  heat_number_mandatory?: boolean;
  mtc_required_on_receipt?: boolean;
}

export interface GrnItemHeatLineDTO {
  id: string;
  grn_item_id?: string;
  heat_number: string;
  dimension?: string;
  quantity: number;
  mtc_document_id?: string | null;
  /** Resolved from documents.document_number when MTC is linked */
  mtc_document_number?: string | null;
}

export interface GRNLineDTO {
  id: string;
  grn_id: string;
  po_item_id: string;
  line_number: number;
  item_code?: string;
  item_description?: string;
  /** Technical specification snapshot from the PO line. */
  specifications?: string | null;
  /** Enriched from item master on read. */
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
  ordered_quantity: number;
  received_quantity: number;
  accepted_quantity: number;
  rejected_quantity: number;
  hold_quantity: number;
  uom?: string;
  project_id?: string;
  warehouse_id?: string;
  notes?: string;
  supplier_description?: string | null;
  stock_item_id?: string | null;
  /** Weighed receipt weight (information only). */
  actual_weight?: number | string | null;
  actual_weight_uom?: string | null;
  purchaseOrderItem?: {
    unit_price?: number;
    specifications?: string | null;
    stock_quantity?: number | null;
    stock_uom?: string | null;
    ordered_quantity?: number | string | null;
    theoretical_weight?: number | string | null;
    engineering_snapshot?: string | null;
  };
  heat_lines?: GrnItemHeatLineDTO[];
  /** Present if API returns raw TypeORM shape */
  heatLines?: GrnItemHeatLineDTO[];
}

/** API may send `heat_lines` (serialized) or `heatLines` (raw TypeORM); support both. */
export function getGrnLineHeatLines(line: GRNLineDTO | Record<string, unknown>): GrnItemHeatLineDTO[] {
  const raw = (line as GRNLineDTO).heat_lines ?? (line as GRNLineDTO).heatLines;
  return Array.isArray(raw) ? raw : [];
}

export interface GRNHeaderDTO {
  id: string;
  company_id: string;
  grn_number: string;
  po_id: string;
  po_milestone_id?: string;
  milestone?: { id: string; milestone_number?: number; milestone_name?: string };
  supplier_id: string;
  grn_date: string;
  receipt_time?: string;
  supplier_delivery_note?: string;
  supplier_invoice_number?: string;
  vehicle_number?: string;
  driver_name?: string;
  warehouse_location?: string;
  warehouse_id?: string;
  received_by: string;
  /** When API joins users — prefer for display over raw `received_by` id */
  received_by_display?: string | null;
  approved_by_display?: string | null;
  project_id?: string;
  department_id?: string | null;
  grn_status: string;
  approval_instance_id?: string | null;
  receipt_type: string;
  /** Critical stock/consumable lines require QC pass before post */
  inspection_required?: boolean;
  inspection_status?: string;
  inspected_by?: string | null;
  inspection_date?: string | null;
  inspection_notes?: string | null;
  /** Linked QMS Receiving InspectionRecord id */
  qc_inspection_record_id?: string | null;
  qc_inspection_number?: string | null;
  is_posted: boolean;
  posted_date?: string;
  gl_posted?: boolean;
  journal_voucher_id?: string;
  grn_credit_account_id?: string;
  grnCreditAccount?: { id: string; account_code?: string; account_name?: string };
  notes?: string;
  created_at?: string;
  supplier?: { id: string; supplier_name?: string };
  purchaseOrder?: { id: string; po_number?: string };
  warehouse?: { id: string; warehouse_name?: string };
  project?: { id: string; project_name?: string };
  items?: GRNLineDTO[];
}

export interface CreateGRNDTO {
  po_id: string;
  po_milestone_id?: string;
  grn_date: string;
  receipt_time?: string;
  supplier_delivery_note?: string;
  supplier_invoice_number?: string;
  vehicle_number?: string;
  driver_name?: string;
  warehouse_id?: string;
  warehouse_location?: string;
  received_by: string;
  project_id?: string;
  department_id?: string;
  notes?: string;
  lines: Array<{
    po_item_id: string;
    received_quantity: number;
    accepted_quantity: number;
    rejected_quantity: number;
    hold_quantity?: number;
    warehouse_id?: string;
    notes?: string;
    stock_item_id?: string | null;
    heat_lines?: Array<{
      heat_number: string;
      dimension: string;
      quantity: number;
      mtc_document_id?: string | null;
    }>;
  }>;
}

export interface GRNFilters {
  grn_number?: string;
  /** Free-text: GRN #, PO #, supplier, or date. */
  search?: string;
  po_number?: string;
  supplier_id?: string;
  warehouse_id?: string;
  status?: string;
  receipt_type?: string;
  date_from?: string;
  date_to?: string;
}

export type CriticalMtcGapReason = 'missing_heat_lines' | 'missing_mtc';

export interface CriticalMtcGapFilters {
  status?: string;
  search?: string;
  gap_reason?: CriticalMtcGapReason;
}

export interface CriticalMtcGapDTO {
  grn_id: string;
  grn_number: string;
  grn_status: string;
  grn_date: string;
  po_number: string | null;
  grn_item_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string | null;
  item_type: string | null;
  accepted_quantity: number;
  heat_number_mandatory: boolean;
  mtc_required_on_receipt: boolean;
  heat_line_id: string | null;
  heat_number: string | null;
  dimension: string | null;
  heat_quantity: number | null;
  mtc_document_id: string | null;
  mtc_document_number: string | null;
  gap_reason: CriticalMtcGapReason;
}

export class GoodsReceiptNoteService {
  async list(filters?: GRNFilters): Promise<GRNHeaderDTO[]> {
    const q = new URLSearchParams();
    if (filters?.grn_number) q.set('grn_number', filters.grn_number);
    if (filters?.search) q.set('search', filters.search);
    if (filters?.po_number) q.set('po_number', filters.po_number);
    if (filters?.supplier_id) q.set('supplier_id', filters.supplier_id);
    if (filters?.warehouse_id) q.set('warehouse_id', filters.warehouse_id);
    if (filters?.status) q.set('status', filters.status);
    if (filters?.receipt_type) q.set('receipt_type', filters.receipt_type);
    if (filters?.date_from) q.set('date_from', filters.date_from);
    if (filters?.date_to) q.set('date_to', filters.date_to);
    const res = await api.get(`/procurement/goods-receipt-notes?${q.toString()}`);
    const raw = unwrap<GRNHeaderDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getReceivableLines(poId: string): Promise<ReceivablePOLineDTO[]> {
    const res = await api.get(`/procurement/goods-receipt-notes/receivable-lines?po_id=${encodeURIComponent(poId)}`);
    const raw = unwrap<ReceivablePOLineDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getPostingDefaults(): Promise<{
    default_credit_account_id: string;
    account_code: string;
    account_name: string;
  }> {
    const res = await api.get('/procurement/goods-receipt-notes/posting-defaults');
    return unwrap(res);
  }

  async getById(id: string): Promise<GRNHeaderDTO> {
    const res = await api.get(`/procurement/goods-receipt-notes/${id}`);
    return unwrap<GRNHeaderDTO>(res);
  }

  async create(dto: CreateGRNDTO): Promise<GRNHeaderDTO> {
    const res = await api.post('/procurement/goods-receipt-notes', dto);
    return unwrap<GRNHeaderDTO>(res);
  }

  async update(
    id: string,
    dto: Partial<CreateGRNDTO> & { lines: CreateGRNDTO['lines'] }
  ): Promise<GRNHeaderDTO> {
    const res = await api.put(`/procurement/goods-receipt-notes/${id}`, dto);
    return unwrap<GRNHeaderDTO>(res);
  }

  async submit(id: string): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/submit`, {});
    return unwrap<GRNHeaderDTO>(res);
  }

  async approve(id: string, remarks?: string): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/approve`, { remarks: remarks ?? undefined });
    return unwrap<GRNHeaderDTO>(res);
  }

  async rejectApproval(id: string, payload: RejectionPayload): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/reject-approval`, payload);
    return unwrap<GRNHeaderDTO>(res);
  }

  async revokeApproval(id: string, reason: string): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/revoke-approval`, { reason });
    return unwrap<GRNHeaderDTO>(res);
  }

  async passInspection(
    id: string,
    body?: {
      notes?: string;
      lines?: Array<{
        grn_item_id: string;
        accepted_quantity?: number;
        rejected_quantity?: number;
        hold_quantity?: number;
      }>;
    }
  ): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/inspection/pass`, body ?? {});
    return unwrap<GRNHeaderDTO>(res);
  }

  async failInspection(
    id: string,
    body: {
      notes: string;
      lines?: Array<{
        grn_item_id: string;
        accepted_quantity?: number;
        rejected_quantity?: number;
        hold_quantity?: number;
      }>;
    }
  ): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/inspection/fail`, body);
    return unwrap<GRNHeaderDTO>(res);
  }

  async getOutsourceAllocationContext(id: string): Promise<{
    grn_id: string;
    lines: Array<{
      grn_item_id: string;
      line_number: number;
      item_description: string;
      received_quantity: number;
      requires_allocation: boolean;
      eligible_units: Array<{
        production_unit_number: number;
        work_order_stage_id: string;
        already_allocated: boolean;
      }>;
    }>;
  }> {
    const res = await api.get(`/procurement/goods-receipt-notes/${id}/outsource-allocation-context`);
    return unwrap(res);
  }

  async post(
    id: string,
    body?: {
      credit_account_id?: string;
      outsource_allocations?: Array<{ grn_item_id: string; unit_numbers: number[] }>;
    }
  ): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/post`, body ?? {});
    return unwrap<GRNHeaderDTO>(res);
  }

  async unpost(id: string): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/unpost`, {});
    return unwrap<GRNHeaderDTO>(res);
  }

  async cancel(id: string): Promise<GRNHeaderDTO> {
    const res = await api.post(`/procurement/goods-receipt-notes/${id}/cancel`, {});
    return unwrap<GRNHeaderDTO>(res);
  }

  async listCriticalMtcGaps(filters?: CriticalMtcGapFilters): Promise<CriticalMtcGapDTO[]> {
    const q = new URLSearchParams();
    if (filters?.status) q.set('status', filters.status);
    if (filters?.search) q.set('search', filters.search);
    if (filters?.gap_reason) q.set('gap_reason', filters.gap_reason);
    const qs = q.toString();
    const res = await api.get(
      `/procurement/goods-receipt-notes/critical-mtc-gaps${qs ? `?${qs}` : ''}`
    );
    const raw = unwrap<CriticalMtcGapDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async setHeatLineMtc(
    grnId: string,
    heatLineId: string,
    mtc_document_id: string | null
  ): Promise<{ heat_line_id: string; mtc_document_id: string | null; mtc_document_number: string | null }> {
    const res = await api.patch(
      `/procurement/goods-receipt-notes/${grnId}/heat-lines/${heatLineId}/mtc`,
      { mtc_document_id }
    );
    return unwrap(res);
  }

  async addRemediationHeatLines(
    grnId: string,
    grnItemId: string,
    heat_lines: Array<{
      heat_number: string;
      dimension: string;
      quantity: number;
      mtc_document_id?: string | null;
    }>
  ): Promise<GRNHeaderDTO> {
    const res = await api.post(
      `/procurement/goods-receipt-notes/${grnId}/items/${grnItemId}/heat-lines`,
      { heat_lines }
    );
    return unwrap<GRNHeaderDTO>(res);
  }

  async getVoucherByGrnId(grnId: string): Promise<any | null> {
    const res = await api.get('/finance/vouchers/by-reference', {
      params: { reference_type: 'GRN', reference_id: grnId },
    });
    const data = (res as any)?.data ?? res;
    return data ?? null;
  }
}

export const goodsReceiptNoteService = new GoodsReceiptNoteService();
